import {
  type Attributes,
  type Context,
  context,
  createContextKey,
  type Histogram,
  logs,
  metrics,
  propagation,
  ROOT_CONTEXT,
  SeverityNumber,
  SpanKind,
  SpanStatusCode,
  trace,
} from "@opentelemetry/api";
import { W3CBaggagePropagator, W3CTraceContextPropagator } from "@opentelemetry/core";
import { formatTraceparent } from "./index";

export interface ClientTelemetrySettings {
  origin: string;
  attributes: () => Attributes;
}

const operationAttributesKey = createContextKey("pax.client.operation.attributes");

let settings: ClientTelemetrySettings | undefined;
const reported = new WeakSet<object>();
const tracer = trace.getTracer("pax.client");
const logger = logs.getLogger("pax.client");
let requestDuration: Histogram | undefined;

export function configureClientTelemetry(value: ClientTelemetrySettings): void {
  settings = value;
}

export function clientAttributes(): Attributes {
  return settings?.attributes() ?? {};
}

function clientErrorType(error: unknown): string {
  if (error && typeof error === "object" && "code" in error && typeof error.code === "string") {
    return error.code;
  }
  return error instanceof Error ? error.name : "unknown_error";
}

function isCancellation(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

/** Exception details are recorded once, even when several operation boundaries see the same error. */
export function reportClientError(
  error: unknown,
  parent = context.active(),
  attributes: Attributes = {},
): void {
  if (isCancellation(error)) return;
  if (error && typeof error === "object") {
    if (reported.has(error)) return;
    reported.add(error);
  }
  const status = error && typeof error === "object" && "status" in error ? error.status : undefined;
  const expected = typeof status === "number" && status >= 400 && status < 500;
  logger.emit({
    context: parent,
    severityNumber: expected ? SeverityNumber.WARN : SeverityNumber.ERROR,
    severityText: expected ? "WARN" : "ERROR",
    body: "Operation failed",
    attributes: {
      ...clientAttributes(),
      ...attributes,
      "error.type": clientErrorType(error),
      "exception.type": error instanceof Error ? error.name : "Error",
      "exception.message": error instanceof Error ? error.message : String(error),
      ...(error instanceof Error && error.stack ? { "exception.stacktrace": error.stack } : {}),
    },
  });
}

export type ClientFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface ClientOperation {
  context: Context;
  traceparent: string;
  fetch: ClientFetch;
  run: <Result>(
    name: string,
    run: (operation: ClientOperation) => Result,
    attributes?: Attributes,
  ) => Result;
  log: (message: string, attributes?: Attributes) => void;
}

/** Carries an operation's context explicitly through native async functions on web and iOS. */
export function clientOperation<Result>(
  name: string,
  run: (operation: ClientOperation) => Result,
  attributes: Attributes = {},
  parent: Context = context.active(),
): Result {
  const initialAttributes = {
    ...clientAttributes(),
    ...(parent.getValue(operationAttributesKey) as Attributes | undefined),
    ...attributes,
  };
  const span = tracer.startSpan(name, { attributes: initialAttributes }, parent);
  const operationContext = trace
    .setSpan(parent, span)
    .setValue(operationAttributesKey, initialAttributes);
  const operation: ClientOperation = {
    context: operationContext,
    traceparent: formatTraceparent({
      ...span.spanContext(),
      sampled: (span.spanContext().traceFlags & 1) === 1,
    }),
    fetch: (input, init) => clientFetch(input, init, operationContext),
    run: (childName, child, childAttributes) =>
      clientOperation(childName, child, childAttributes, operationContext),
    log: (message, fields = {}) =>
      logger.emit({
        context: operationContext,
        severityNumber: SeverityNumber.INFO,
        severityText: "INFO",
        body: message,
        attributes: { ...initialAttributes, ...fields },
      }),
  };
  const failed = (error: unknown) => {
    if (!isCancellation(error)) {
      span.setAttribute("error.type", clientErrorType(error));
      span.setStatus({ code: SpanStatusCode.ERROR });
      reportClientError(error, operationContext, {
        ...initialAttributes,
        "pax.operation.name": name,
      });
    }
    span.end();
  };
  try {
    const result = context.with(operationContext, () => run(operation));
    if (result instanceof Promise) {
      return result.then(
        value => {
          span.end();
          return value;
        },
        error => {
          failed(error);
          throw error;
        },
      ) as Result;
    }
    span.end();
    return result;
  } catch (error) {
    failed(error);
    throw error;
  }
}

/** A UI action begins a new trace; its requests may be made through the supplied scoped fetch. */
export function clientAction<Result>(
  name: string,
  run: (operation: ClientOperation) => Result,
  attributes: Attributes = {},
): Result {
  return clientOperation(name, run, { ...attributes, "pax.action": true }, ROOT_CONTEXT);
}

/** Request policy stays at the shared transport, so page loading does not need an action wrapper. */
export async function clientFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  parent = context.active(),
): Promise<Response> {
  const request = new Request(
    typeof input === "string" && settings ? new URL(input, settings.origin) : input,
    init,
  );
  const url = new URL(request.url);
  if (
    !settings ||
    url.origin !== settings.origin ||
    !/^\/(api|drafts|games|asset-editor)(\/|$)/.test(url.pathname) ||
    /^\/api\/(telemetry|activity)\//.test(url.pathname)
  ) {
    return fetch(request);
  }
  const method = request.method;
  const initialAttributes = {
    ...clientAttributes(),
    ...(parent.getValue(operationAttributesKey) as Attributes | undefined),
  };
  const span = tracer.startSpan(
    method,
    {
      kind: SpanKind.CLIENT,
      attributes: {
        ...initialAttributes,
        "http.request.method": method,
        "server.address": url.hostname,
        "url.full": `${url.origin}${url.pathname}`,
      },
    },
    parent,
  );
  const sessionId = initialAttributes["session.id"];
  if (typeof sessionId === "string") {
    const baggage = propagation.createBaggage({ "session.id": { value: sessionId } });
    new W3CBaggagePropagator().inject(propagation.setBaggage(parent, baggage), request.headers, {
      set: (headers, key, value) => headers.set(key, value),
    });
  }
  new W3CTraceContextPropagator().inject(trace.setSpan(parent, span), request.headers, {
    set: (headers, key, value) => headers.set(key, value),
  });
  const start = performance.now();
  const metricAttributes: Attributes = {
    "http.request.method": method,
    "server.address": url.hostname,
  };
  let finished = false;
  const finish = (error?: unknown) => {
    if (finished) return;
    finished = true;
    if (error && !isCancellation(error)) {
      const type = clientErrorType(error);
      metricAttributes["error.type"] = type;
      span.setAttribute("error.type", type);
      span.setStatus({ code: SpanStatusCode.ERROR });
      reportClientError(error, trace.setSpan(parent, span), initialAttributes);
    }
    requestDuration ??= metrics
      .getMeter("pax.client")
      .createHistogram("http.client.request.duration", {
        unit: "s",
        description: "Duration of a first-party HTTP request, including its response body.",
        advice: {
          explicitBucketBoundaries: [
            0.005, 0.01, 0.025, 0.05, 0.075, 0.1, 0.25, 0.5, 0.75, 1, 2.5, 5, 7.5, 10,
          ],
        },
      });
    requestDuration.record((performance.now() - start) / 1000, metricAttributes);
    span.end();
  };
  try {
    const response = await fetch(request);
    span.setAttribute("http.response.status_code", response.status);
    metricAttributes["http.response.status_code"] = response.status;
    if (response.status >= 400) {
      metricAttributes["error.type"] = String(response.status);
      span.setAttribute("error.type", String(response.status));
      span.setStatus({ code: SpanStatusCode.ERROR });
    }
    if (!response.body) {
      finish();
      return response;
    }
    const reader = response.body.getReader();
    const body = new ReadableStream<Uint8Array>({
      async pull(controller) {
        try {
          const next = await reader.read();
          if (next.done) {
            finish();
            controller.close();
          } else controller.enqueue(next.value);
        } catch (error) {
          finish(error);
          controller.error(error);
        }
      },
      async cancel(reason) {
        finish();
        await reader.cancel(reason);
      },
    });
    const traced = new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
    for (const key of ["url", "redirected", "type"] as const) {
      Object.defineProperty(traced, key, { value: response[key] });
    }
    return traced;
  } catch (error) {
    finish(error);
    throw error;
  }
}
